<template>
  <b-modal
    id="automation-configuration-modal"
    ref="automation-configuration-modal"
    size="md"
    centered
    @show="handleModalShow"
    :title="$t('fleetTaskInstant.autoEndedQuickAssigment.completionAutomationConfiguration')"
    class="mc-setting-auto-ended-quick-assignment"
  >
    <div class="card-body">
      <div>
        <div
          v-if="pointConfig?.isEndPoint"
          v-html="$t('fleetTaskInstant.autoEndedQuickAssigment.completeOnArrivalAtEndingPoint2')"
        />
        <div
          v-else
          v-html="
            $t('fleetTaskInstant.autoEndedQuickAssigment.completeOnDepartureFromLastLocation2')
          "
        />
        <div class="mc-setting-auto-ended-quick-assignment__point-chip">
          <span class="mc-setting-auto-ended-quick-assignment__point-icon">
            <span v-if="pointConfig?.isEndPoint">
              <i class="icon-fill_flag" />
            </span>
            <span v-else>
              {{ pointConfig?.lastPointOrder }}
            </span>
          </span>
          <p class="mc-setting-auto-ended-quick-assignment__point-text">
            {{
              pointConfig?.isEndPoint
                ? pointConfig?.endPoint?.address?.name
                : pointConfig?.lastPoint?.address?.name
            }}
          </p>
        </div>
      </div>

      <hr />

      <div>
        <div class="mc-setting-auto-ended-quick-assignment__rule">
          <div class="mc-setting-auto-ended-quick-assignment__rule-copy">
            <McCheckbox
              id="require-all-locations-visited"
              v-model="autoEndedAllVisited"
              :label="texts.requireAllLocationsVisited"
            />
          </div>
        </div>
        <div class="mc-setting-auto-ended-quick-assignment__rule">
          <div class="mc-setting-auto-ended-quick-assignment__rule-main">
            <div class="mc-setting-auto-ended-quick-assignment__rule-copy">
              <McCheckbox
                id="use-max-active-limit"
                v-model="autoEndedDurationEnabled"
                :label="texts.useMaxActiveLimit"
              />
            </div>

            <div
              v-if="autoEndedDurationEnabled"
              class="mc-setting-auto-ended-quick-assignment__inline-control"
            >
              <div class="mc-setting-auto-ended-quick-assignment__input-group">
                <input
                  v-model.number="completeAfterDays"
                  class="mc-setting-auto-ended-quick-assignment__days-input"
                  :class="{
                    'mc-setting-auto-ended-quick-assignment__days-input--error':
                      showDurationValidationError,
                  }"
                  type="number"
                  :min="minActiveDays"
                  :max="maxActiveDays"
                  @input="handleDurationInput"
                  @blur="handleDurationBlur"
                />
                <span
                  v-if="showDurationValidationError"
                  class="mc-setting-auto-ended-quick-assignment__error"
                >
                  {{ durationValidationErrorText }}
                </span>
              </div>
              <span class="mc-setting-auto-ended-quick-assignment__inline-text">
                {{ texts.completeAfterDaysSuffix }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #modal-footer>
      <div class="d-flex mx-0">
        <McButton variant="secondary" class="me-2" :text="texts.cancel" @click="handleCancel" />
        <McButton
          variant="primary"
          :text="texts.save"
          :disabled="!hasSettingChanged || isLoading"
          @click="handleSave"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import isEqual from 'lodash/isEqual'
import { mapState, mapGetters } from 'vuex'

import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import * as Setting from '@/services/rest-api/lib/fleet-task/setting'

const MIN_ACTIVE_DAYS = 7
const MAX_ACTIVE_DAYS = 30
const ACTIVITIES_COMPLETION = 'ACTIVITIES_COMPLETION'

export default {
  components: {
    McButton,
    McCheckbox,
  },
  props: {
    vehicle: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      autoEndedToLastPoint: true,
      autoEndedAllVisited: true,
      autoEndedDurationEnabled: false,
      completeAfterDays: MIN_ACTIVE_DAYS,
      durationInputTouched: false,
      initialSetting: null,
      isLoading: false,
      minActiveDays: MIN_ACTIVE_DAYS,
      maxActiveDays: MAX_ACTIVE_DAYS,
      pointConfig: null,
      listPointPlan: null,
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', ['showResponse']),
    texts() {
      return {
        title: this.$t('fleetTaskInstant.autoEndedQuickAssigment.label'),
        cancel: this.$t('fleetTaskInstant.autoEndedQuickAssigment.cancelButton'),
        save: this.$t('fleetTaskInstant.autoEndedQuickAssigment.saveButton'),
        tripAutomation: this.$t('fleetTaskInstant.autoEndedQuickAssigment.tripAutomation'),
        tripAutomationDescription: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.tripAutomationDescription'
        ),
        completeAutomation: this.$t('fleetTaskInstant.autoEndedQuickAssigment.completeAutomation'),
        completeAutomationDescription: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.completeAutomationDescription'
        ),
        additionalRules: this.$t('fleetTaskInstant.autoEndedQuickAssigment.additionalRules'),
        requireAllLocationsVisited: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.requireAllLocationsVisited'
        ),
        useMaxActiveLimit: this.$t('fleetTaskInstant.autoEndedQuickAssigment.useMaxActiveLimit'),
        completeAfterDaysPrefix: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.completeAfterDaysPrefix'
        ),
        completeAfterDaysSuffix: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.completeAfterDaysSuffix'
        ),
        defaultActiveStatus: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.defaultActiveStatus'
        ),
        defaultInactiveStatus: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.defaultInactiveStatus'
        ),
      }
    },
    hasSettingChanged() {
      if (this.status === 'TEMPORARY') {
        return true
      }
      if (!this.initialSetting) {
        return false
      }

      return !isEqual(this.initialSetting, this.getCurrentForm())
    },
    isDurationInvalid() {
      if (!this.autoEndedDurationEnabled) {
        return false
      }

      const parsedValue = Number(this.completeAfterDays)

      if (this.completeAfterDays === '' || Number.isNaN(parsedValue)) {
        return true
      }

      return parsedValue < this.minActiveDays || parsedValue > this.maxActiveDays
    },
    showDurationValidationError() {
      return this.durationInputTouched && this.isDurationInvalid
    },
    durationValidationErrorText() {
      const parsedValue = Number(this.completeAfterDays)

      if (this.completeAfterDays === '' || Number.isNaN(parsedValue)) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.validation.minimumDays')
      }

      if (parsedValue < this.minActiveDays) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.validation.minimumDays')
      }

      if (parsedValue > this.maxActiveDays) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.validation.maximumDays')
      }

      return ''
    },
    status() {
      return this.showResponse?.status?.raw_type || 'TEMPORARY'
    },
  },
  watch: {
    autoEndedDurationEnabled(value) {
      if (value) {
        return
      }

      this.completeAfterDays = MIN_ACTIVE_DAYS
      this.durationInputTouched = false
    },
  },
  methods: {
    async handleModalShow() {
      const showResponseConfiguration = this.getShowResponseConfiguration()
      const qaConfiguration = this.getQaConfigurationFromLocalStorage()

      if (showResponseConfiguration) {
        let resolvedShowResponseConfiguration = { ...showResponseConfiguration }
        const hasShowResponseAutoEndType = resolvedShowResponseConfiguration.auto_end_type != null

        if (
          resolvedShowResponseConfiguration.auto_end_type == null &&
          this.hasConfigurationKey(qaConfiguration, 'auto_end_type')
        ) {
          resolvedShowResponseConfiguration.auto_end_type = qaConfiguration.auto_end_type
        }

        if (
          !hasShowResponseAutoEndType &&
          this.getShowResponseDuration(resolvedShowResponseConfiguration) == null &&
          this.hasConfigurationKey(qaConfiguration, 'auto_end_by_duration')
        ) {
          resolvedShowResponseConfiguration.auto_end_by_duration =
            qaConfiguration.auto_end_by_duration
        }

        const shouldLoadSetting =
          (resolvedShowResponseConfiguration.auto_end_type == null &&
            !this.hasConfigurationKey(qaConfiguration, 'auto_end_type')) ||
          (!hasShowResponseAutoEndType &&
            this.getShowResponseDuration(resolvedShowResponseConfiguration) == null &&
            !this.hasConfigurationKey(qaConfiguration, 'auto_end_by_duration'))

        if (shouldLoadSetting) {
          const setting = await this.getUserSetting({ shouldApply: false })
          const autoEndedDefault = setting?.auto_ended_default || {}

          if (
            resolvedShowResponseConfiguration.auto_end_type == null &&
            !this.hasConfigurationKey(qaConfiguration, 'auto_end_type')
          ) {
            resolvedShowResponseConfiguration.auto_end_type =
              autoEndedDefault.auto_end_type ||
              (autoEndedDefault.auto_ended_all_visited ? 'ALL_VISITED' : 'LAST_POINT')
          }

          if (
            !hasShowResponseAutoEndType &&
            this.getShowResponseDuration(resolvedShowResponseConfiguration) == null &&
            !this.hasConfigurationKey(qaConfiguration, 'auto_end_by_duration')
          ) {
            resolvedShowResponseConfiguration.auto_end_by_duration =
              autoEndedDefault.auto_ended_duration ?? null
          }
        }

        this.applyShowResponseConfiguration(resolvedShowResponseConfiguration)
      } else if (qaConfiguration) {
        this.applyQaConfiguration(qaConfiguration)
      } else {
        await this.getUserSetting()
      }

      this.getPointsData()
    },
    async getPointsData() {
      const res = await FleetTaskInstant.getPoints(this.$route.params.id, {
        is_order_by_actual: false,
      })

      if (res.errors) {
        this.$mcToast.error(
          this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
        )
        return null
      }

      this.listPointPlan = res.data

      this.getPointConfig()
    },
    getPointConfig() {
      const points = this.listPointPlan?.points || []
      const startPoint = points.find((point) => point.type === 'START')
      const endPoint = points.find((point) => point.type === 'END')
      const lastPoint = points[points.length - 1] ?? null
      const isHasStartPoint = !!startPoint

      this.pointConfig = {
        endPoint: endPoint ? endPoint : null,
        isEndPoint: !!endPoint,
        startPoint: startPoint ? startPoint : null,
        isStartPoint: !!startPoint,
        lastPoint: lastPoint ? lastPoint : null,
        lastPointOrder: lastPoint ? points.length - (isHasStartPoint ? 1 : 0) : null,
        isHasStartPoint: isHasStartPoint,
      }
    },
    async getUserSetting({ shouldApply = true } = {}) {
      this.isLoading = true

      try {
        const res = await Setting.get()

        if (res?.errors) {
          this.$mcToast.error(
            this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
          )
          return null
        }

        if (shouldApply) {
          this.applySetting(res?.data)
        }

        return res?.data || null
      } catch (error) {
        this.$mcToast.error(
          this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
        )
        return null
      } finally {
        this.isLoading = false
      }
    },
    applySetting(setting) {
      const normalizedSetting = this.normalizeSetting(setting)

      this.autoEndedToLastPoint = normalizedSetting.autoEndedToLastPoint
      this.autoEndedAllVisited = normalizedSetting.autoEndedAllVisited
      this.autoEndedDurationEnabled = normalizedSetting.autoEndedDurationEnabled
      this.completeAfterDays = normalizedSetting.completeAfterDays
      this.durationInputTouched = false
      this.initialSetting = { ...normalizedSetting }
    },
    applyQaConfiguration(setting) {
      const normalizedSetting = this.normalizeQaConfiguration(setting)

      this.autoEndedToLastPoint = normalizedSetting.autoEndedToLastPoint
      this.autoEndedAllVisited = normalizedSetting.autoEndedAllVisited
      this.autoEndedDurationEnabled = normalizedSetting.autoEndedDurationEnabled
      this.completeAfterDays = normalizedSetting.completeAfterDays
      this.durationInputTouched = false
      this.initialSetting = { ...normalizedSetting }
    },
    applyShowResponseConfiguration(setting) {
      const normalizedSetting = this.normalizeShowResponseConfiguration(setting)

      this.autoEndedToLastPoint = normalizedSetting.autoEndedToLastPoint
      this.autoEndedAllVisited = normalizedSetting.autoEndedAllVisited
      this.autoEndedDurationEnabled = normalizedSetting.autoEndedDurationEnabled
      this.completeAfterDays = normalizedSetting.completeAfterDays
      this.durationInputTouched = false
      this.initialSetting = { ...normalizedSetting }
    },
    normalizeSetting(setting = {}) {
      const autoEndedDefault = setting.auto_ended_default || {}

      return {
        autoEndedToLastPoint: autoEndedDefault.auto_ended_to_last_point ?? true,
        autoEndedAllVisited:
          autoEndedDefault.auto_end_type === ACTIVITIES_COMPLETION ||
          !!autoEndedDefault.auto_ended_all_visited,
        autoEndedDurationEnabled: Boolean(autoEndedDefault.auto_ended_duration),
        completeAfterDays: this.parseDurationDays(autoEndedDefault.auto_ended_duration),
      }
    },
    normalizeShowResponseConfiguration(setting = {}) {
      const duration = this.getShowResponseDuration(setting)

      return {
        autoEndedToLastPoint: !this.isAllVisitedAutoEndType(setting.auto_end_type),
        autoEndedAllVisited: this.isAllVisitedAutoEndType(setting.auto_end_type),
        autoEndedDurationEnabled: Boolean(duration),
        completeAfterDays: this.parseDurationDays(duration),
      }
    },
    normalizeQaConfiguration(setting = {}) {
      return {
        autoEndedToLastPoint: !this.isAllVisitedAutoEndType(setting.auto_end_type),
        autoEndedAllVisited: this.isAllVisitedAutoEndType(setting.auto_end_type),
        autoEndedDurationEnabled: Boolean(setting.auto_end_by_duration),
        completeAfterDays: this.parseDurationDays(setting.auto_end_by_duration),
      }
    },
    isAllVisitedAutoEndType(type) {
      return type === 'ALL_VISITED' || type === ACTIVITIES_COMPLETION
    },
    getShowResponseConfiguration() {
      const hasShowResponseShape =
        Object.prototype.hasOwnProperty.call(this.showResponse || {}, 'auto_end_type') ||
        Object.prototype.hasOwnProperty.call(this.showResponse || {}, 'auto_end_by_duration')

      if (!hasShowResponseShape) {
        return null
      }

      return this.showResponse
    },
    getShowResponseDuration(configuration) {
      return configuration?.auto_end_by_duration ?? null
    },
    getQaConfigurationFromLocalStorage() {
      try {
        const storedConfiguration = localStorage.getItem('qaConfiguration')

        if (!storedConfiguration) {
          return null
        }

        const parsedConfiguration = JSON.parse(storedConfiguration)

        if (!parsedConfiguration || typeof parsedConfiguration !== 'object') {
          return null
        }

        const hasQaConfigurationShape =
          Object.prototype.hasOwnProperty.call(parsedConfiguration, 'auto_end_type') ||
          Object.prototype.hasOwnProperty.call(parsedConfiguration, 'auto_end_by_duration')

        if (!hasQaConfigurationShape) {
          return null
        }

        return parsedConfiguration
      } catch (error) {
        return null
      }
    },
    hasConfigurationKey(configuration, key) {
      return !!configuration && Object.prototype.hasOwnProperty.call(configuration, key)
    },
    parseDurationDays(duration) {
      if (typeof duration !== 'string') {
        return MIN_ACTIVE_DAYS
      }

      const parsedValue = Number(duration.replace('P', '').replace('D', ''))
      if (Number.isNaN(parsedValue)) {
        return MIN_ACTIVE_DAYS
      }

      return parsedValue
    },
    getCurrentForm() {
      return {
        autoEndedToLastPoint: this.autoEndedToLastPoint,
        autoEndedAllVisited: this.autoEndedAllVisited,
        autoEndedDurationEnabled: this.autoEndedDurationEnabled,
        completeAfterDays: this.completeAfterDays,
      }
    },
    getPayload() {
      return {
        auto_end_type: this.autoEndedAllVisited ? 'ALL_VISITED' : 'LAST_POINT',
        auto_end_by_duration: this.autoEndedDurationEnabled
          ? `P${Number(this.completeAfterDays)}D`
          : null,
      }
    },
    handleDurationInput() {
      this.durationInputTouched = true
    },
    handleDurationBlur() {
      this.durationInputTouched = true
    },
    handleCancel() {
      if (this.initialSetting) {
        this.autoEndedToLastPoint = this.initialSetting.autoEndedToLastPoint
        this.autoEndedAllVisited = this.initialSetting.autoEndedAllVisited
        this.autoEndedDurationEnabled = this.initialSetting.autoEndedDurationEnabled
        this.completeAfterDays = this.initialSetting.completeAfterDays
        this.durationInputTouched = false
      }

      this.$bvModal.hide('automation-configuration-modal')
    },

    handleSave() {
      if (this.isDurationInvalid) {
        this.durationInputTouched = true
        return
      }

      this.isLoading = true

      try {
        const payload = this.getPayload()
        this.$emit('save-automation-configuration', {
          payload,
          onSuccess: () => {
            localStorage.setItem('qaConfiguration', JSON.stringify(payload))
            this.initialSetting = { ...this.getCurrentForm() }
            this.durationInputTouched = false
            this.isLoading = false
            this.$bvModal.hide('automation-configuration-modal')
          },
          onError: () => {
            this.isLoading = false
            this.$mcToast.error(
              this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
            )
          },
        })
      } catch (error) {
        this.isLoading = false
        this.$mcToast.error(
          this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
        )
      }
    },
  },
}
</script>

<style scoped lang="scss">
.mc-setting-auto-ended-quick-assignment {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
    color: $teal;
    font-size: 18px;
    font-weight: 600;
    line-height: 32px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__card {
    border: 1px solid $teal-100;
    border-radius: 5px;
    background: $white;
    padding: 16px 20px;
  }

  &__card-title {
    margin: 0;
    color: $teal;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  &__description {
    margin: 4px 0 0;
    color: #667085;
    font-size: 14px;
    line-height: 22px;
  }

  &__rules {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
  }

  &__rules-title {
    color: $teal;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  &__rule {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__point-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    background: $white;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  }

  &__point-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background: #2f80ed;
    flex-shrink: 0;

    > span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      text-align: center;
    }

    span {
      color: $white;
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
    }
    i {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      line-height: 1;
    }

    i::before {
      color: $white;
    }
  }

  &__point-text {
    margin: 0;
    color: $teal-300;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  &__rule-main {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__rule-copy {
    display: flex;
    flex-direction: column;
  }

  &__helper {
    color: $teal-300;
    font-size: 12px;
    line-height: 18px;
    margin-top: -5px;
    padding-left: 16px;
  }

  &__inline-control {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__inline-text {
    color: $teal-300;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
  }

  &__input-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  &__days-input {
    width: 68px;
    height: 28px;
    padding: 4px 10px;
    border: 1px solid #d0d5dd;
    border-radius: 4px;
    color: #344054;
    font-size: 12px;
    line-height: 18px;

    &:focus {
      outline: none;
    }
  }

  &__days-input--error {
    border-color: $red;
  }

  &__error {
    color: $red;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
  }
}

:deep(.mc-setting-auto-ended-quick-assignment .custom-checkbox) {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
}

:deep(.mc-setting-auto-ended-quick-assignment .custom-control-label) {
  margin-left: 0;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

:deep(.mc-setting-auto-ended-quick-assignment .custom-control-label::before),
:deep(.mc-setting-auto-ended-quick-assignment .custom-control-label::after) {
  top: 2px;
}

@media (max-width: 768px) {
  .mc-setting-auto-ended-quick-assignment {
    &__title {
      font-size: 20px;
      line-height: 28px;
    }

    &__header,
    &__rule-main {
      align-items: stretch;
    }

    &__actions {
      width: 100%;
    }

    &__helper {
      padding-left: 0;
    }
  }
}
</style>

